import React, { useState, useEffect } from "react";
import { locationsAPI } from "../lib/api";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  MapPin,
  PlusCircle,
  Trash2,
  Edit,
  Loader2,
  X,
  Archive,
} from "lucide-react";

export function LocationsPage() {
  const [locations, setLocations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editLocation, setEditLocation] = useState(null);
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState(null);

  const [formData, setFormData] = useState({
    name: "",
    description: "",
  });

  const showMessage = (text, type = "success") => {
    setMessage({ text, type });
    setTimeout(() => setMessage(null), 3000);
  };

  useEffect(() => {
    loadLocations();
  }, []);

  const loadLocations = async () => {
    try {
      const response = await locationsAPI.getAll();
      setLocations(response.data);
    } catch (error) {
      showMessage("Errore nel caricamento delle ubicazioni", "error");
    } finally {
      setLoading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!formData.name) {
      showMessage("Il nome è obbligatorio", "error");
      return;
    }

    setSaving(true);
    try {
      if (editLocation) {
        await locationsAPI.update(editLocation.id, formData);
        showMessage("Ubicazione aggiornata");
      } else {
        await locationsAPI.create(formData);
        showMessage("Ubicazione creata");
      }
      setShowForm(false);
      setEditLocation(null);
      setFormData({ name: "", description: "" });
      loadLocations();
    } catch (error) {
      showMessage(error.response?.data?.detail || "Errore", "error");
    } finally {
      setSaving(false);
    }
  };

  const handleEdit = (location) => {
    setEditLocation(location);
    setFormData({
      name: location.name,
      description: location.description || "",
    });
    setShowForm(true);
  };

  const handleDelete = async (location) => {
    if (!window.confirm(`Eliminare l'ubicazione "${location.name}"?`)) return;
    try {
      await locationsAPI.delete(location.id);
      showMessage("Ubicazione eliminata");
      loadLocations();
    } catch (error) {
      showMessage("Errore durante l'eliminazione", "error");
    }
  };

  if (loading) {
    return (
      <div className="max-w-4xl mx-auto p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-muted rounded w-1/3"></div>
          <div className="h-64 bg-muted rounded"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6 animate-fade-in" data-testid="locations-page">
      {/* Message Banner */}
      {message && (
        <div className={`fixed top-4 right-4 z-50 px-4 py-3 rounded-lg shadow-lg ${
          message.type === "error" ? "bg-red-500 text-white" : "bg-emerald-500 text-white"
        }`}>
          {message.text}
        </div>
      )}

      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-heading font-bold text-foreground flex items-center gap-3">
            <MapPin size={32} className="text-[#2D7BB7]" />
            Ubicazioni Documenti
          </h1>
          <p className="text-muted-foreground mt-1">
            Gestisci le ubicazioni fisiche dei documenti protocollati
          </p>
        </div>
        <Button
          onClick={() => {
            setEditLocation(null);
            setFormData({ name: "", description: "" });
            setShowForm(true);
          }}
          className="bg-[#2D7BB7] hover:bg-[#5BA4D4] text-white"
          data-testid="add-location-btn"
        >
          <PlusCircle className="mr-2" size={18} />
          Nuova Ubicazione
        </Button>
      </div>

      {/* Form Modal */}
      {showForm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setShowForm(false)} />
          <Card className="relative z-50 w-full max-w-md mx-4 bg-card">
            <CardHeader>
              <CardTitle className="flex items-center justify-between">
                <span>{editLocation ? "Modifica Ubicazione" : "Nuova Ubicazione"}</span>
                <Button variant="ghost" size="icon" onClick={() => setShowForm(false)}>
                  <X size={20} />
                </Button>
              </CardTitle>
            </CardHeader>
            <CardContent>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="space-y-2">
                  <Label>Nome *</Label>
                  <Input
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    placeholder="Es: Archivio A - Scaffale 1"
                    data-testid="location-name-input"
                  />
                </div>

                <div className="space-y-2">
                  <Label>Descrizione</Label>
                  <textarea
                    value={formData.description}
                    onChange={(e) => setFormData({ ...formData, description: e.target.value })}
                    placeholder="Descrizione opzionale..."
                    className="w-full min-h-[80px] px-3 py-2 rounded-md border border-input bg-background text-foreground"
                  />
                </div>

                <div className="flex justify-end gap-2 pt-4">
                  <Button type="button" variant="outline" onClick={() => setShowForm(false)}>
                    Annulla
                  </Button>
                  <Button
                    type="submit"
                    disabled={saving}
                    className="bg-[#2D7BB7] hover:bg-[#5BA4D4] text-white"
                  >
                    {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : editLocation ? "Salva" : "Crea"}
                  </Button>
                </div>
              </form>
            </CardContent>
          </Card>
        </div>
      )}

      {/* Locations List */}
      <Card className="bg-card border-border">
        <CardHeader>
          <CardTitle className="text-lg font-heading text-foreground">
            {locations.length} ubicazioni
          </CardTitle>
        </CardHeader>
        <CardContent>
          {locations.length === 0 ? (
            <div className="text-center py-12 text-muted-foreground">
              <Archive size={48} className="mx-auto mb-4 opacity-50" />
              <p>Nessuna ubicazione configurata</p>
              <p className="text-sm mt-1">Aggiungi la prima ubicazione per i documenti fisici</p>
            </div>
          ) : (
            <div className="space-y-3">
              {locations.map((location) => (
                <div
                  key={location.id}
                  className="flex items-center justify-between p-4 bg-muted rounded-lg hover:bg-muted/80 transition-colors"
                  data-testid={`location-item-${location.id}`}
                >
                  <div className="flex items-center gap-4">
                    <div className="p-2 rounded-lg bg-amber-100 text-amber-700">
                      <MapPin size={20} />
                    </div>
                    <div>
                      <p className="font-medium text-foreground">{location.name}</p>
                      {location.description && (
                        <p className="text-sm text-muted-foreground">{location.description}</p>
                      )}
                    </div>
                  </div>

                  <div className="flex items-center gap-2">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => handleEdit(location)}
                      className="border-border"
                    >
                      <Edit size={16} />
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => handleDelete(location)}
                      className="text-red-600 hover:text-red-700 hover:bg-red-50"
                    >
                      <Trash2 size={16} />
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
